import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth.store'

export const Support: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [subject, setSubject] = useState('')
  const [priority, setPriority] = useState('medium')
  const [message, setMessage] = useState('')
  const [formMessage, setFormMessage] = useState('')
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null)
  const [reply, setReply] = useState('')
  const [replyError, setReplyError] = useState('')

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['support-tickets'],
    queryFn: async () => {
      try {
        const response = await api.get('/tickets')
        return response.data.data || []
      } catch (error) {
        return []
      }
    },
  })

  const { data: replies, isLoading: repliesLoading, isError: repliesError } = useQuery({
    queryKey: ['ticket-replies', selectedTicket?.id],
    queryFn: async () => {
      const response = await api.get(`/tickets/${selectedTicket.id}/replies`)
      return response.data.data || []
    },
    enabled: !!selectedTicket,
  })

  const replyMutation = useMutation({
    mutationFn: async (content: string) => {
      const response = await api.post(`/tickets/${selectedTicket.id}/replies`, { content })
      return response.data
    },
    onSuccess: () => {
      setReply('')
      setReplyError('')
      queryClient.invalidateQueries({ queryKey: ['ticket-replies', selectedTicket?.id] })
    },
    onError: (error: any) => {
      setReplyError(error.response?.data?.error || t('support.replyError'))
    },
  })

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!reply.trim()) return
    replyMutation.mutate(reply.trim())
  }

  const createTicketMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/tickets', data)
      return response.data
    },
    onSuccess: () => {
      setSubject('')
      setMessage('')
      setPriority('medium')
      setFormMessage(t('support.ticketSuccess'))
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      setTimeout(() => setFormMessage(''), 5000)
    },
    onError: (error: any) => {
      setFormMessage(error.response?.data?.error || t('support.ticketError'))
      setTimeout(() => setFormMessage(''), 5000)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!subject.trim() || !message.trim()) {
      setFormMessage(t('support.fillAllFields'))
      return
    }
    createTicketMutation.mutate({ subject, description: message, priority })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-green-500/20 text-green-400'
      case 'pending': return 'bg-yellow-500/20 text-yellow-400'
      case 'resolved': return 'bg-blue-500/20 text-blue-400'
      case 'closed': return 'bg-gray-500/20 text-gray-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-400'
      case 'high': return 'text-orange-400'
      case 'medium': return 'text-yellow-400'
      case 'low': return 'text-green-400'
      default: return 'text-gray-400'
    }
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('support.title')}</h1>

      <div className="bg-[#006cd4]/10 border border-[#006cd4]/20 rounded-lg p-4 mb-8">
        <p className="text-sm text-gray-300">
          💡 {t('support.checkHelp')}{' '}
          <Link to="/help" className="text-[#006cd4] hover:underline">
            {t('support.checkHelpLink')}
          </Link>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Ticket Detail / Ticket Form */}
        {selectedTicket ? (
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <button
              onClick={() => { setSelectedTicket(null); setReply(''); setReplyError('') }}
              className="text-sm text-[#006cd4] hover:text-[#0052a3] mb-4 transition-colors"
            >
              ← {t('support.backToForm')}
            </button>

            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold">{selectedTicket.subject}</h2>
              <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(selectedTicket.status)}`}>
                {selectedTicket.status}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs mb-4">
              <span className={`capitalize ${getPriorityColor(selectedTicket.priority)}`}>
                {t('support.priorityLabel', { priority: selectedTicket.priority })}
              </span>
              <span className="text-gray-500">
                {format(new Date(selectedTicket.created_at), 'PPp')}
              </span>
            </div>
            <p className="text-sm text-gray-400 mb-6 whitespace-pre-wrap">{selectedTicket.description}</p>

            <h3 className="text-sm font-medium text-gray-300 mb-3">{t('support.replies')}</h3>
            {repliesLoading ? (
              <div className="text-gray-400 text-center py-4">{t('support.loadingReplies')}</div>
            ) : repliesError ? (
              <div className="text-red-400 text-center py-4">{t('support.repliesLoadError')}</div>
            ) : !replies || replies.length === 0 ? (
              <div className="text-gray-400 text-center py-4 text-sm">{t('support.noReplies')}</div>
            ) : (
              <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
                {replies.map((r: any) => {
                  const isOwn = r.user_id === user?.id
                  return (
                    <div key={r.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        isOwn
                          ? 'bg-[#006cd4]/20 border border-[#006cd4]/30'
                          : 'bg-[#1a1a25] border border-white/10'
                      }`}>
                        <div className="flex items-center gap-2 mb-1 text-xs">
                          <span className="font-medium">
                            {isOwn ? t('support.you') : (r.author_name || t('support.supportTeam'))}
                          </span>
                          <span className="text-gray-500">{format(new Date(r.created_at), 'PPp')}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{r.content}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {replyError && (
              <div className="mb-4 p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">
                {replyError}
              </div>
            )}

            <form onSubmit={handleReplySubmit} className="space-y-3">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
                placeholder={t('support.replyPlaceholder')}
                required
              />
              <button
                type="submit"
                disabled={replyMutation.isPending}
                className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
              >
                {replyMutation.isPending ? t('support.sending') : t('support.sendReply')}
              </button>
            </form>
          </div>
        ) : (
        /* Ticket Form */
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-6">{t('support.submitTicket')}</h2>

          {formMessage && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              formMessage.includes(t('support.ticketSuccess'))
                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {formMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('support.subject')}</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
                placeholder={t('support.subjectPlaceholder')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('support.priority')}</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]"
              >
                <option value="low">{t('support.low')}</option>
                <option value="medium">{t('support.medium')}</option>
                <option value="high">{t('support.high')}</option>
                <option value="urgent">{t('support.urgent')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('support.message')}</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
                placeholder={t('support.messagePlaceholder')}
                required
              />
            </div>
            <button
              type="submit"
              disabled={createTicketMutation.isPending}
              className="w-full py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
            >
              {createTicketMutation.isPending ? t('support.submitting') : t('support.submit')}
            </button>
          </form>
        </div>
        )}

        {/* Ticket History */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-6">{t('support.yourTickets')}</h2>
          {isLoading ? (
            <div className="text-gray-400 text-center py-8">{t('support.loadingTickets')}</div>
          ) : !tickets || tickets.length === 0 ? (
            <div className="text-gray-400 text-center py-8">
              <p>{t('support.noTicketsTitle')}</p>
              <p className="text-sm mt-1">{t('support.noTicketsDesc')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket: any) => (
                <div
                  key={ticket.id}
                  onClick={() => { setSelectedTicket(ticket); setReply(''); setReplyError('') }}
                  className={`p-4 bg-[#1a1a25] rounded-lg cursor-pointer transition-colors hover:border-white/20 border ${
                    selectedTicket?.id === ticket.id ? 'border-[#006cd4]' : 'border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">{ticket.subject}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs capitalize ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-2 line-clamp-2">{ticket.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`capitalize ${getPriorityColor(ticket.priority)}`}>
                      {t('support.priorityLabel', { priority: ticket.priority })}
                    </span>
                    <span className="text-gray-500">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-8 bg-[#12121a] border border-white/10 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-6">{t('support.faq')}</h2>
        <div className="space-y-4">
          <div className="p-4 bg-[#1a1a25] rounded-lg">
            <h3 className="font-medium mb-2">{t('support.faq1')}</h3>
            <p className="text-sm text-gray-400">{t('support.faq1Answer')}</p>
          </div>
          <div className="p-4 bg-[#1a1a25] rounded-lg">
            <h3 className="font-medium mb-2">{t('support.faq2')}</h3>
            <p className="text-sm text-gray-400">{t('support.faq2Answer')}</p>
          </div>
          <div className="p-4 bg-[#1a1a25] rounded-lg">
            <h3 className="font-medium mb-2">{t('support.faq3')}</h3>
            <p className="text-sm text-gray-400">{t('support.faq3Answer')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
